import { defineStore } from 'pinia';
import { usePinia } from '#imports';
import { handleErrorAndNotificationHandler } from '~/util/errorNotificationHandler';
import { AxiosError, isAxiosError } from 'axios';
import type { Pinia } from './shared/sharedTypes';

interface Address {
  addressId: number;
  buildingName?: string;
  countryId: number;
  countryName: string;
  locality?: string;
  postalCode?: string;
  region?: string;
  streetAddressLine1?: string;
  streetAddressLine2?: string;
  streetAddressLine3?: string;
}

interface Phone {
  contactTypeDisplayName: string;
  extension?: string;
  number: string;
  phoneId: number;
  primary: boolean;
}

interface Contact {
  address?: Address;
  contactEmailAddress?: string;
  contactName?: string;
  contactTypeDisplayName?: string;
  countryId?: number;
  countryName?: string;
  locality?: string;
  postalCode?: string;
  primary?: boolean;
  region?: string;
  streetAddressLine1?: string;
  streetAddressLine2?: string;
  streetAddressLine3?: string;
  contactType?: string;
  phone?: Phone;
}

interface NotificationContact {
  contactEmailAddress: string;
  contactName: string;
}

interface StudyTeamCompliance {
  complianceTrackingEnabled: boolean;
}

interface WorkflowStatusOption {
  statusName: string;
  statusNameLabel: string;
  updateStatusUrl: string;
}

interface Workflow {
  statusOptions: WorkflowStatusOption[];
}
interface StudyTeam {
  actions: Record<string, { href: string; method: string }>;
  activeMemberCount: number;
  channelId: string | number;
  channelName: string;
  contacts: {
    contactData: Contact[];
    contactTypes: string[];
    primaryContactTypeForTeam: string;
  };
  createDateTimeMillis: number;
  createdByUserId: number;
  displayName: string;
  isActive: boolean;
  isInvestigatorSite: boolean;
  name: string;
  notificationContacts: NotificationContact[];
  participantType: string;
  roleId: number;
  statusId: number;
  statusName: string;
  studyTeamCompliance: StudyTeamCompliance;
  studyTeamDetailUrl: string;
  studyTeamId: number;
  teamType: string;
  workflow: Workflow;
  organizationId: number;
}

export interface TeamDetail {
  code?: string;
  countryId?: number;
  countryName?: string;
  principalInvestigatorName?: string;
  siteActivationDateTimeMillis?: number;
  siteClosureDateTimeMillis?: number;
  siteSelectionDateTimeMillis?: number;
  siteInitationVisitDateTimeMillis?: number;
  studySiteId?: number;
  studyTeam: StudyTeam;
}

export interface StudyTeamListItem {
  studyTeam: StudyTeam;
  name?: string;
  statusName?: string;
  channelRoleId?: number;
  teamType?: string;
  title?: string;
}

interface StudyTeamsList {
  items: StudyTeamListItem[];
  page: number;
  pageSize: number;
  totalCount: number;
}

interface RawStudyTeamItem {
  actions?: Record<string, { href: string; method: string }>;
  activeMemberCount?: number;
  channelId?: string | number;
  channelName?: string;
  contacts?: StudyTeam['contacts'];
  primaryContactData?: {
    address?: Address;
    phone?: Phone;
  };
  createDateTimeMillis?: number;
  createdByUserId?: number;
  displayName?: string;
  isActive?: boolean;
  isInvestigatorSite?: boolean;
  name?: string;
  notificationContacts?: NotificationContact[];
  participantType?: string;
  roleId?: number;
  statusId?: number;
  statusName?: string;
  studyTeamCompliance?: StudyTeamCompliance;
  studyTeamDetailUrl?: string;
  studyTeamId?: number;
  teamType?: string;
  workflow?: Workflow;
  investigatorSiteCode?: string;
  investigatorSiteCountryName?: string;
  investigatorSitePrincipalInvestigatorName?: string;
  investigatorSiteCountryId?: number;
  organizationId?: number;
}

interface TeamOversightData {
  overseenByTeamIds: number[];
}

interface ChannelsAPI {
  searchChannelStudyTeams(
    channelId: string | number,
    queryParams: Record<string, unknown>
  ): string;
  channelTeamDetail(
    channelId: string | number,
    teamId: string | number
  ): string;
  channelTeamOversight(
    channelId: string | number,
    teamId: string | number
  ): string;
  actionTeamOversight(
    channelId: string | number,
    teamId: string | number
  ): string;
  editTeamInfo(channelId: string | number, teamId: string | number): string;
  updateStatusStudyTeam(
    channelId: string | number,
    teamId: string | number,
    queryParams: Record<string, unknown>
  ): string;
  createInvestigatorSite(channelId: string | number): string;
  getStudyTeamActions(channelId: number, teamId: number): string;
  assignPrimaryTeamContact(
    channelId: string | number,
    channelMemberId: string,
    teamId: string | number
  ): string;

  unassignPrimaryTeamContact(
    channelId: string | number,
    memberId: string | number,
    teamId: string | number
  ): string;
  addPrimaryTeamContact(
    channelId: string | number,
    teamId: string | number
  ): string;
}

interface API {
  channels: ChannelsAPI;
}

function mappedTeam(resp: TeamDetail) {
  const team = resp.studyTeam;
  return {
    code: resp.code,
    countryId: resp.countryId,
    countryName: resp.countryName,
    principalInvestigatorName: resp.principalInvestigatorName,
    siteActivationDateTimeMillis: resp.siteActivationDateTimeMillis,
    siteClosureDateTimeMillis: resp.siteClosureDateTimeMillis,
    siteSelectionDateTimeMillis: resp.siteSelectionDateTimeMillis,
    siteInitationVisitDateTimeMillis: resp.siteInitationVisitDateTimeMillis,
    studySiteId: resp.studySiteId,
    studyTeam: team,
    name: team?.name,
    statusName: team?.statusName,
    channelRoleId: team?.roleId,
    teamType: team?.teamType,
    title: team?.displayName,
  };
}

export const useTeamsStore = defineStore('teams', {
  state: () => ({
    studyTeams: {} as Record<string, StudyTeamsList>,
    teamDetail: {} as Record<string, TeamDetail>,
    oversightAction: '' as 'POST' | 'DELETE' | 'PUT' | '',
    teamType: null as string | null,
    otherTeamName: '',
    addressAction: '',
    addressInfo: {} as Address,
    teamOversightData: {} as Record<string, TeamOversightData>,
    suppressToast: false,
    teamSearchTerm: '' as string,
    teamPagination: null as {
      skip: number;
      take: number;
      pageSizeValue: number | string;
      sort: string;
    } | null,
  }),
  actions: {
    updateStudyTeams(payload: TeamDetail) {
      if (!payload?.studyTeam) return;

      const key = `${payload.studyTeam.channelId}>${payload.studyTeam.teamType}`;

      if (!this.studyTeams[key]) return;
      const originalArray = [...this.studyTeams[key].items];
      const index = originalArray.findIndex(
        (i) => i.studyTeam.studyTeamId === payload.studyTeam.studyTeamId
      );

      const mapped = mappedTeam(payload);

      if (index === -1) {
        this.studyTeams[key].items.push(mapped);
      } else {
        this.studyTeams[key].items.splice(index, 1, mapped);
      }
    },
    async fetchStudyTeamActions(channelId: number, teamId: number) {
      const pinia = usePinia() as Pinia<API>;
      try {
        const resp: Record<string, { href: string; method: string }> =
          await pinia.$axios.$get(
            pinia.$API.channels.getStudyTeamActions(channelId, teamId)
          );
        const key = `${channelId}>${teamId}`;
        const team = this.teamDetail[key];
        if (team?.studyTeam) {
          team.studyTeam.actions = resp;
        }
      } catch (error) {
        console.log('error', error);
      }
    },
    async searchChannelTeams({
      page,
      pageSize,
      sort,
      teamType,
      channelId,
      search,
    }: {
      page: number;
      pageSize: number;
      sort: string;
      teamType: string;
      channelId: string | number;
      search: string;
    }) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;
        const resp: StudyTeamsList = await pinia.$axios.$post(
          pinia.$API.channels.searchChannelStudyTeams(
            channelId,
            new Query({
              page,
              pageSize,
              sort,
              teamType,
              search,
            }).getQueryParameters()
          ),
          []
        );

        const key = `${channelId}>${teamType}`;
        const formattedItems = resp.items.map((item: RawStudyTeamItem) => ({
          studyTeam: {
            actions: item.actions || {},
            activeMemberCount: item.activeMemberCount || 0,
            channelId: item.channelId || '',
            channelName: item.channelName || '',
            contacts: item.contacts ? item.contacts : item.primaryContactData,
            createDateTimeMillis: item.createDateTimeMillis || 0,
            createdByUserId: item.createdByUserId || 0,
            displayName: item.displayName || '',
            isActive: item.isActive ?? false,
            isInvestigatorSite: item.isInvestigatorSite ?? false,
            name: item.name || '',
            notificationContacts: item.notificationContacts || [],
            participantType: item.participantType || '',
            roleId: item.roleId || 0,
            statusId: item.statusId || 0,
            statusName: item.statusName || '',
            studyTeamCompliance: item.studyTeamCompliance ?? {
              complianceTrackingEnabled: false,
            },
            studyTeamDetailUrl: item.studyTeamDetailUrl || '',
            studyTeamId: item.studyTeamId || 0,
            teamType: item.teamType || '',
            workflow: item.workflow || { statusOptions: [] },
            organizationId: item.organizationId || 0,
          },
          name: item.name,
          statusName: item.statusName,
          teamType: item.teamType,
          code: item.investigatorSiteCode,
          countryName: item.investigatorSiteCountryName,
          principalInvestigatorName:
            item.investigatorSitePrincipalInvestigatorName,
          countryId: item.investigatorSiteCountryId,
        }));
        this.studyTeams[key] = {
          ...resp,
          items: formattedItems as StudyTeamListItem[],
        };
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error searching channel teams:',
          error
        );
      }
    },

    async channelTeamDetail({
      channelId,
      teamId,
    }: {
      channelId: string | number;
      teamId: string | number;
    }) {
      try {
        const pinia = usePinia() as Pinia<API>;

        const resp: TeamDetail = await pinia.$axios.$get(
          pinia.$API.channels.channelTeamDetail(channelId, teamId)
        );
        const key = `${channelId}>${teamId}`;

        this.teamDetail[key] = resp;
      } catch (error: unknown) {
        if (error instanceof AxiosError && error.response?.status === 403) {
          return;
        }
        handleErrorAndNotificationHandler(
          'Error fetching channel team detail:',
          error
        );
      }
    },
    async mutateTeamsTable(payload: {
      channelId: string | number;
      mappedData: { teamId: string | number; eventType: 'CREATE' | 'UPDATE' };
    }) {
      try {
        const pinia = usePinia() as Pinia<API>;
        console.log('mutateTeamsTable payload:', payload);

        const { channelId, mappedData } = payload;
        const { teamId, eventType } = mappedData;
        const resp: TeamDetail = await pinia.$axios.$get(
          pinia.$API.channels.channelTeamDetail(channelId, teamId)
        );

        const team = mappedTeam(resp);

        const key = `${channelId}>${team.studyTeam.teamType}`;
        if (!this.studyTeams[key]) {
          return;
        }

        const index = this.studyTeams[key].items?.findIndex(
          (item) => item.studyTeam.studyTeamId === team.studyTeam.studyTeamId
        );

        if (eventType === 'CREATE') {
          if (index === -1 || index === undefined) {
            this.studyTeams[key].items.push(team);
            this.studyTeams[key].totalCount =
              (this.studyTeams[key].totalCount || 0) + 1;
          } else {
            this.studyTeams[key].items.splice(index, 1, team);
          }
        } else if (eventType === 'UPDATE') {
          if (index === -1 || index === undefined) return;
          this.studyTeams[key].items.splice(index, 1, team);
        }
      } catch (error: unknown) {
        if (isAxiosError(error)) {
          if (error.response?.status === 403) {
            return;
          }
          handleErrorAndNotificationHandler(
            'Error mutating teams table:',
            error
          );
        } else {
          handleErrorAndNotificationHandler('Unexpected error:', error);
        }
      }
    },

    async teamOversight({
      channelId,
      teamId,
    }: {
      channelId: string | number;
      teamId: string | number;
    }) {
      try {
        const pinia = usePinia() as Pinia<API>;

        const resp: TeamOversightData = await pinia.$axios.$get(
          pinia.$API.channels.channelTeamOversight(channelId, teamId)
        );

        const key = `${channelId}>${teamId}`;
        this.teamOversightData[key] = resp;
      } catch (error) {
        handleErrorAndNotificationHandler('Error in team oversight:', error);
      }
    },

    async actionTeamOversight({
      channelId,
      teamId,
      oversightTeamIds,
    }: {
      channelId: string | number;
      teamId: string | number;
      oversightTeamIds: number[];
    }) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const apiFunction = {
          POST: pinia.$axios.$post,
          DELETE: pinia.$axios.$delete,
          PUT: pinia.$axios.$put,
        }[this.oversightAction];

        if (apiFunction) {
          const payload =
            this.oversightAction === 'DELETE'
              ? { data: { overseenByTeamIds: oversightTeamIds } }
              : { overseenByTeamIds: oversightTeamIds };

          await apiFunction(
            pinia.$API.channels.actionTeamOversight(channelId, teamId),
            payload
          );
        }
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in action team oversight:',
          error
        );
      }
    },

    oversightActionType(action: 'POST' | 'DELETE' | 'PUT') {
      this.oversightAction = action;
    },

    setTeamType(teamType: string) {
      this.teamType = teamType;
    },

    setTeamSearchTerm(payload: string) {
      this.teamSearchTerm = payload;
    },

    setTeamPagination(
      payload: {
        skip: number;
        take: number;
        pageSizeValue: number | string;
        sort: string;
      } | null
    ) {
      this.teamPagination = payload;
    },

    supressToastMessageForTeam(event: boolean) {
      this.suppressToast = event;
    },

    setOtherTeamName(name: string) {
      this.otherTeamName = name;
    },

    addressActionType(action: string) {
      this.addressAction = action;
    },

    addressInfoUpdate(info: Address) {
      this.addressInfo = info;
    },

    async saveContactData({
      address,
      params,
    }: {
      address: {
        save: (params: object) => Promise<void>;
        patch: (params: object) => Promise<void>;
      };
      params: object;
    }) {
      try {
        if (this.addressAction === 'POST') await address.save(params);
        else if (this.addressAction === 'PATCH') await address.patch(params);
      } catch (error) {
        handleErrorAndNotificationHandler('Error saving contact data:', error);
      }
    },

    async deleteContact({
      address,
      params,
    }: {
      address: { delete: (params: object) => Promise<void> };
      params: object;
    }) {
      try {
        await address.delete(params);
      } catch (error) {
        handleErrorAndNotificationHandler('Error deleting contact:', error);
      }
    },

    async updateStatusStudyTeam({
      channelId,
      teamId,
      statusName,
    }: {
      channelId: string | number;
      teamId: string | number;
      statusName: string;
    }) {
      try {
        const pinia = usePinia() as Pinia<API>;
        const Query = pinia.$Query;
        await pinia.$axios.$patch(
          pinia.$API.channels.updateStatusStudyTeam(
            channelId,
            teamId,
            new Query({ transitionName: statusName }).getQueryParameters()
          )
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error updating status study team:',
          error
        );
      }
    },

    async updateInvestigatorTeam({
      team,
      params,
    }: {
      team: { update: (params: object) => Promise<void> };
      params: object;
    }) {
      try {
        await team.update(params);
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error updating investigator team:',
          error
        );
      }
    },

    async updateOtherTeams({
      channelId,
      teamId,
      teamName,
      teamType,
      complianceTrackingEnabled,
    }: {
      channelId: string | number;
      teamId: string | number;
      teamName: string;
      teamType: string;
      complianceTrackingEnabled: boolean;
    }) {
      try {
        const pinia = usePinia() as Pinia<API>;

        await pinia.$axios.$patch(
          pinia.$API.channels.editTeamInfo(channelId, teamId),
          { name: teamName, teamType, complianceTrackingEnabled }
        );
      } catch (error) {
        handleErrorAndNotificationHandler('Error updating other teams:', error);
      }
    },

    async createTeamByType({
      team,
      params,
    }: {
      team: { saveOtherTeamType: (params: object) => Promise<void> };
      params: object;
    }) {
      try {
        await team.saveOtherTeamType(params);
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error creating team by type:',
          error
        );
      }
    },

    async createStudySite({
      channelId,
      payload,
    }: {
      channelId: string | number;
      payload: object;
    }) {
      try {
        const pinia = usePinia() as Pinia<API>;
        await pinia.$axios.$post(
          pinia.$API.channels.createInvestigatorSite(channelId),
          payload
        );
      } catch (error) {
        handleErrorAndNotificationHandler('Error creating study site:', error);
        throw error;
      }
    },

    async setPrimaryTeamContact({
      channelId,
      teamId,
      memberId,
      oldPrimaryChannelMemberId,
    }: {
      channelId: string | number;
      teamId: string | number;
      memberId: number;
      oldPrimaryChannelMemberId?: string | null;
    }) {
      try {
        const pinia = usePinia() as Pinia<API>;

        if (oldPrimaryChannelMemberId) {
          await pinia.$axios.$delete(
            pinia.$API.channels.unassignPrimaryTeamContact(
              Number(channelId),
              oldPrimaryChannelMemberId,
              Number(teamId)
            )
          );
        }

        const newChannelMemberId = `${teamId}-${memberId}`;
        await pinia.$axios.$patch(
          pinia.$API.channels.assignPrimaryTeamContact(
            Number(channelId),
            newChannelMemberId,
            Number(teamId)
          )
        );

        await this.channelTeamDetail({ channelId, teamId });
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error setting primary team contact:',
          error
        );
      }
    },
    async createTeamPrimaryContact({
      channelId,
      teamId,
      payload,
    }: {
      channelId: string;
      teamId: string;
      payload: object;
    }) {
      const pinia = usePinia() as Pinia<API>;
      try {
        await pinia.$axios.$put(
          pinia.$API.channels.addPrimaryTeamContact(channelId, teamId),
          payload
        );
      } catch (error) {
        handleErrorAndNotificationHandler(
          'Error in creating new primary contact.',
          error
        );
      }
    },
  },
  getters: {
    getStudyTeamsGetter: (state) => state.studyTeams,
    getTeamDetailGetter: (state) => state.teamDetail,
    getTeamOversightGetter: (state) => state.teamOversightData,
  },
});
